import { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import type { Tool } from '@tools-beaver/shared';
import { MascotSection } from '../components/landing/MascotSection';
import { AskTim } from '../components/landing/AskTim';
import { NavIcons } from '../components/landing/NavIcons';
import { ToolList } from '../components/tools/ToolList';

const WHATSAPP_NUMBER = import.meta.env.VITE_WHATSAPP_NUMBER ?? '10000000000';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi! I found Tools Beaver and want to share it with my community 🦫')}`;

export function Landing() {
  const [results, setResults] = useState<Tool[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const handleResults = (tools: Tool[]) => {
    setResults(tools);
    setHasSearched(true);
  };

  return (
    <main className="min-h-[calc(100dvh-3.5rem)] flex flex-col">
      {/* Hero section */}
      <section className="flex-1 flex flex-col items-center justify-center px-4 py-8 sm:py-12
                           bg-gradient-to-b from-beaver-linen via-white to-white">
        <div className="w-full max-w-2xl mx-auto flex flex-col items-center gap-6">
          {/* Mascot + branding */}
          <MascotSection />

          {/* Ask Tim search */}
          <AskTim onResults={handleResults} />

          {/* Navigation icons */}
          <NavIcons />
        </div>
      </section>

      {/* Search results */}
      {hasSearched && (
        <section className="px-4 py-6 max-w-6xl mx-auto w-full animate-slide-up">
          <ToolList
            tools={results}
            title={results.length > 0 ? `${results.length} tools found nearby` : undefined}
            emptyMessage="No tools found nearby yet — but check back soon as more neighbors join!"
          />
        </section>
      )}

      {/* WhatsApp community share */}
      <section className="px-4 py-8 bg-gradient-to-br from-garden-50 to-beaver-beige">
        <div className="max-w-sm mx-auto text-center">
          <div className="inline-flex items-center gap-3 bg-white rounded-3xl px-5 py-4 shadow-card
                          hover:shadow-card-hover transition-all duration-200 cursor-pointer group"
            onClick={() => window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer')}
            aria-label="Share Tools Beaver with your community on WhatsApp"
          >
            {/* WhatsApp logo */}
            <div className="w-10 h-10 rounded-2xl bg-[#25D366] flex items-center justify-center
                            flex-shrink-0 group-hover:scale-110 transition-transform">
              <svg viewBox="0 0 24 24" fill="white" className="w-6 h-6">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div className="text-left">
              <p className="font-semibold text-silver-800 text-sm">Share with your community</p>
              <p className="text-silver-400 text-xs">Invite neighbors to share tools</p>
            </div>
            <MessageCircle size={16} className="text-silver-300 flex-shrink-0 ml-auto" />
          </div>
        </div>
      </section>

      {/* Feature highlights */}
      <section className="px-4 py-10 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-display font-extrabold text-2xl text-silver-800 text-center mb-8">
            Why Tools Beaver?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                emoji: '💰',
                title: 'Save Money',
                desc: 'Rent instead of buy. Tools at a fraction of the cost.',
              },
              {
                emoji: '🌿',
                title: 'Reduce Waste',
                desc: 'One drill shared by many. Better for the planet.',
              },
              {
                emoji: '🏘️',
                title: 'Build Community',
                desc: 'Meet your neighbors. Foster trust and connection.',
              },
            ].map((feat) => (
              <div key={feat.title} className="text-center p-6 rounded-3xl bg-beaver-linen
                                               hover:bg-garden-50 transition-colors group">
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform inline-block">
                  {feat.emoji}
                </div>
                <h3 className="font-display font-bold text-silver-800 mb-2">{feat.title}</h3>
                <p className="text-silver-500 text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-silver-800 text-silver-300 text-xs text-center py-6 px-4">
        <p className="mb-1">
          © {new Date().getFullYear()} Tools Beaver · Share. Rent. Build.
        </p>
        <p className="space-x-3">
          <a href="/terms" className="hover:text-white transition-colors">Terms</a>
          <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
          <a href="/help" className="hover:text-white transition-colors">Help</a>
        </p>
      </footer>
    </main>
  );
}
